<script context="module">
  /** A blank row, in the shape both the editor and lib/totals.js read. */
  export function emptyLineItem() {
    return { descrizione: '', quantita: '1', prezzoUnitario: '', aliquotaIva: '22.00', natura: '', unitaMisura: 'NR' };
  }

  /** The rates the select offers. Anything else already on a row is kept. */
  export const IVA_RATES = ['22.00', '10.00', '5.00', '4.00', '0.00'];

  /**
   * Read a stored DettaglioLinee back into an editor row.
   *
   * The API stores the rate as `aliquotaIVA` (that is the FatturaPA spelling)
   * while the editor writes `aliquotaIva`; a row loaded through the wrong one
   * would silently reappear as 22,00% on a document that is taxed at 10.
   */
  export function lineItemFromStored(stored) {
    return {
      descrizione: stored?.descrizione || '',
      quantita: stored?.quantita != null ? String(stored.quantita) : '',
      prezzoUnitario: stored?.prezzoUnitario != null ? String(stored.prezzoUnitario) : '',
      aliquotaIva: String(stored?.aliquotaIva ?? stored?.aliquotaIVA ?? '22.00'),
      natura: stored?.natura || '',
      unitaMisura: stored?.unitaMisura || '',
      esigibilitaIVA: stored?.esigibilitaIVA || '',
    };
  }
</script>

<script>
  import { Trash2, Plus, AlertTriangle } from '../lib/icons/index.js';
  import { t } from '../i18n/index.js';
  import NaturaIvaSelect from './NaturaIvaSelect.svelte';
  import { requiresNatura } from '../lib/natura.js';

  /**
   * The DettaglioLinee editor, shared by the creation screen and by the
   * correction form on a scartata invoice.
   *
   * It was the creation screen's table until the correction form needed the
   * same thing: two tables that compute a row total each is exactly how the
   * figure on screen and the figure in the XML come apart, and lib/totals.js
   * exists so that cannot happen. The per-row totals are therefore passed in,
   * never recomputed here.
   */
  export let lineItems = [];
  /** The result of computeInvoiceTotals() over `lineItems`. */
  export let totals = { lineTotalTexts: [] };
  /** Validation issues to print under a row, keyed by its zero-based index. */
  export let issuesByLine = {};
  /** Rows the operator may never delete below. */
  export let minRows = 1;

  function addLineItem() { lineItems = [...lineItems, emptyLineItem()]; }

  function removeLineItem(i) {
    if (lineItems.length <= minRows) return;
    lineItems = lineItems.filter((_, idx) => idx !== i);
  }

  /**
   * The rate options for one row: the usual five, plus whatever that row
   * already carries. An imported or corrected invoice can be taxed at a rate
   * this list never had (21,00% on an old document), and a select with no
   * matching option shows blank — which reads as "no VAT" on a document that
   * charges it.
   */
  function rateOptions(item) {
    const current = String(item?.aliquotaIva ?? item?.aliquotaIVA ?? '');
    return current && !IVA_RATES.includes(current) ? [...IVA_RATES, current] : IVA_RATES;
  }
</script>

<div class="overflow-x-auto">
  <table class="w-full text-sm">
    <thead>
      <tr class="border-b-w" style="border-color: var(--color-tint-medium);">
        <th class="text-left py-2 px-1 text-xs font-bold uppercase" style="color: var(--color-text-muted);">#</th>
        <th class="text-left py-2 px-1 text-xs font-bold uppercase" style="color: var(--color-text-muted);">{$t('invoiceCreate.fields.description')}</th>
        <th class="text-right py-2 px-1 text-xs font-bold uppercase w-20" style="color: var(--color-text-muted);">{$t('invoiceCreate.fields.quantity')}</th>
        <th class="text-right py-2 px-1 text-xs font-bold uppercase w-28" style="color: var(--color-text-muted);">{$t('invoiceCreate.fields.price')}</th>
        <th class="text-right py-2 px-1 text-xs font-bold uppercase w-20" style="color: var(--color-text-muted);">{$t('invoiceCreate.fields.vatPercent')}</th>
        <th class="text-right py-2 px-1 text-xs font-bold uppercase w-16" style="color: var(--color-text-muted);">{$t('invoiceCreate.fields.unitMeasure')}</th>
        <th class="text-right py-2 px-1 text-xs font-bold uppercase w-28" style="color: var(--color-text-muted);">{$t('invoiceCreate.fields.total')}</th>
        <th class="w-8"></th>
      </tr>
    </thead>
    <tbody>
      {#each lineItems as item, i}
        <tr class="border-b" style="border-color: var(--color-tint-soft);">
          <td class="py-1 px-1 font-mono text-xs" style="color: var(--color-text-muted);">{i + 1}</td>
          <td class="py-1 px-1">
            <input aria-label={$t('invoiceCreate.lineItemField', { campo: $t('invoiceCreate.fields.description'), riga: i + 1 })} bind:value={item.descrizione} class="input input-compact" />
          </td>
          <td class="py-1 px-1">
            <input aria-label={$t('invoiceCreate.lineItemField', { campo: $t('invoiceCreate.fields.quantity'), riga: i + 1 })} bind:value={item.quantita} type="number" step="0.01" min="0" class="input input-compact text-right tabular" />
          </td>
          <td class="py-1 px-1">
            <input aria-label={$t('invoiceCreate.lineItemField', { campo: $t('invoiceCreate.fields.price'), riga: i + 1 })} bind:value={item.prezzoUnitario} type="number" step="0.01" min="0" class="input input-compact text-right tabular" />
          </td>
          <td class="py-1 px-1">
            <select aria-label={$t('invoiceCreate.lineItemField', { campo: $t('invoiceCreate.fields.vatPercent'), riga: i + 1 })} bind:value={item.aliquotaIva} class="input input-compact text-right tabular">
              {#each rateOptions(item) as rate}<option value={rate}>{rate}</option>{/each}
            </select>
          </td>
          <td class="py-1 px-1">
            <input aria-label={$t('invoiceCreate.lineItemField', { campo: $t('invoiceCreate.fields.unitMeasure'), riga: i + 1 })} bind:value={item.unitaMisura} class="input input-compact" />
          </td>
          <td class="py-1 px-1 text-right font-mono text-sm" style="color: var(--color-text-body);">
            {totals.lineTotalTexts?.[i] || '0.00'}
          </td>
          <td class="py-1 px-1">
            {#if lineItems.length > minRows}
              <button type="button" on:click={() => removeLineItem(i)} class="p-1 hover:text-danger" style="color: var(--color-text-muted);"
                title={$t('common.delete')} aria-label={$t('invoices.correction.removeLine', { n: i + 1 })}>
                <Trash2 size={14} />
              </button>
            {/if}
          </td>
        </tr>
        <!-- What the Sistema di Interscambio, or our own generator, refuses
             about this exact row — printed under it rather than in a list the
             operator has to map back onto the table themselves. -->
        {#if issuesByLine[i]?.length}
          <tr>
            <td colspan="8" class="px-1 pb-2">
              {#each issuesByLine[i] as issue}
                <p class="flex gap-2 text-xs measure text-warning" role="status">
                  <AlertTriangle size={14} class="flex-shrink-0 mt-0.5" />
                  <span>{issue}</span>
                </p>
              {/each}
            </td>
          </tr>
        {/if}
      {/each}
    </tbody>
  </table>
</div>

<button type="button" on:click={addLineItem} class="mt-3 flex items-center gap-1 eyebrow hover:text-action transition-colors" style="color: var(--color-text-muted);">
  <Plus size={14} /> {$t('invoiceCreate.fields.addRow')}
</button>

<!-- Natura IVA: required by law on any 0%-VAT line. Shown only then. -->
{#each lineItems as item, i}
  {#if requiresNatura(item.aliquotaIva)}
    <div class="mt-4 border-t pt-3" style="border-color: var(--color-tint-soft);">
      <span class="eyebrow" style="color: var(--color-text-muted);">{$t('invoiceCreate.fields.naturaLineLabel', { n: i + 1 })}</span>
      <div class="mt-1">
        <NaturaIvaSelect bind:value={item.natura} vatRate={item.aliquotaIva} />
      </div>
    </div>
  {/if}
{/each}
